import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ShipmentDetailModal from '../components/deliverer/ShipmentDetailModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'deliverer_token';

const dax = axios.create({ baseURL: API });
dax.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem(TOKEN_KEY);
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

/**
 * Desglose de la deuda del entregador (mercancía + envíos que debe cobrar
 * y entregar al admin). balance_owed es la fuente de verdad del sistema.
 */
export default function DelivererDebt({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailId, setDetailId] = useState(null); // id del envío abierto
  const [detailData, setDetailData] = useState(null); // shipment completo pedido bajo demanda
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await dax.get('/public/deliverer/debt');
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'No se pudo cargar la deuda');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Cuando se abre un item, jalamos el detalle completo desde /pending-shipments
  // (reusamos el mismo endpoint porque ya devuelve items expandidos, productos, etc.)
  useEffect(() => {
    if (!detailId) { setDetailData(null); return; }
    let cancel = false;
    (async () => {
      setDetailLoading(true);
      try {
        const { data } = await dax.get('/public/deliverer/pending-shipments');
        if (cancel) return;
        const found = (data.shipments || []).find((s) => s.id === detailId);
        setDetailData(found || null);
      } catch {
        if (!cancel) setDetailData(null);
      } finally {
        if (!cancel) setDetailLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [detailId]);

  const balance = Number(data?.balance_owed || 0);
  const computed = Number(data?.computed_total || 0);
  const diff = Math.abs(balance - computed);
  const showMismatch = data && diff > 0.5; // avisa si hay descuadre >50¢

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-red-600 text-white px-4 pb-3 shadow flex items-center gap-3" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}>
        <button onClick={onBack} className="text-white/90 hover:text-white text-3xl leading-none w-10 h-10 flex items-center justify-center -ml-2 rounded-full active:bg-red-700" data-testid="debt-back-btn" aria-label="Volver">‹</button>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wide opacity-80">Portal Entregador</p>
          <h1 className="text-base sm:text-lg font-bold truncate">💰 Mi deuda</h1>
        </div>
      </header>

      {error && <div className="bg-rose-50 border-b border-rose-200 px-4 py-2 text-xs text-rose-800" data-testid="debt-error">{error}</div>}

      <div className="max-w-3xl w-full mx-auto p-4 flex-1">
        {loading && <p className="text-sm text-slate-500 text-center py-8">Cargando…</p>}

        {data && (
          <>
            <div className="bg-gradient-to-br from-red-600 to-red-700 text-white rounded-2xl p-5 shadow-lg mb-4" data-testid="debt-summary">
              <p className="text-xs uppercase tracking-wide opacity-80">Debo entregar al administrador</p>
              <p className="text-4xl font-bold mt-1">${balance.toFixed(2)}</p>
              <p className="text-xs mt-2 opacity-90">
                {data.count} envío(s) pendiente(s) de cobro
              </p>
            </div>

            {showMismatch && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-4" data-testid="debt-mismatch">
                ⚠️ La deuda registrada (<b>${balance.toFixed(2)}</b>) no coincide exactamente
                con la suma de tus envíos pendientes (<b>${computed.toFixed(2)}</b>).
                Diferencia: ${diff.toFixed(2)}. Habla con el administrador.
              </div>
            )}

            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Desglose por envío</p>

            {data.shipments.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6" data-testid="debt-empty">Sin envíos pendientes 🎉</p>
            ) : (
              <ul className="space-y-2" data-testid="debt-list">
                {data.shipments.map((s) => (
                  <li key={s.id} className="bg-white border border-slate-200 rounded-lg p-3 hover:border-red-300 cursor-pointer transition" data-testid={`debt-item-${s.id}`} onClick={() => setDetailId(s.id)}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 break-words">{s.client_name || 'Cliente'}</p>
                        {s.folio && <p className="text-[11px] text-slate-400">#{s.folio}</p>}
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-600 mt-1">
                          <span>📦 Mercancía: ${Number(s.items_total).toFixed(2)}</span>
                          {s.deliverer_keeps_shipping ? (
                            <span className="text-emerald-600">🚚 Envío se queda contigo</span>
                          ) : (
                            <span>🚚 Envío: ${Number(s.shipping_cost).toFixed(2)}</span>
                          )}
                          {s.delivered_at && <span className="text-emerald-600">✅ Entregado</span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold text-red-700">${Number(s.owed).toFixed(2)}</p>
                        <p className="text-[10px] text-slate-400">Ver detalle ›</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <ShipmentDetailModal
        shipment={detailLoading ? { client_name: 'Cargando…', items: [] } : detailData}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
}
